import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { UploadSimple, DownloadSimple, FileText, Trash, Eye, X, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { user } = useAuth();

  // Check if user has full access (can edit/delete/upload/download)
  const hasFullAccess = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';

  useEffect(() => {
    loadDocuments();
    loadProjects();
  }, [categoryFilter, selectedProject]);

  const loadDocuments = async () => {
    try {
      let url = '/documents?';
      if (categoryFilter) url += `category=${categoryFilter}&`;
      if (selectedProject) url += `related_id=${selectedProject}`;
      const response = await api.get(url);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.get('/apqp/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        let url = `/documents/upload?category=${uploadCategory}`;
        if (uploadProjectId) url += `&related_id=${uploadProjectId}`;
        
        await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} document(s) uploaded successfully`);
      loadDocuments();
    }
    if (failCount > 0) {
      toast.error(`${failCount} document(s) failed to upload`);
    }

    setUploading(false);
    setShowUploadModal(false);
    e.target.value = '';
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    }
  };

  const handlePreview = async (doc) => {
    if (!doc.content_type?.startsWith('image/')) {
      toast.error('Preview only available for images');
      return;
    }
    
    setPreviewDoc(doc);
    setPreviewLoading(true);
    
    try {
      const response = await api.get(`/documents/${doc.id}/preview`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPreviewUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted successfully');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const isImage = (doc) => doc.content_type?.startsWith('image/');
  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.project_name : '-';
  };

  return (
    <div data-testid="documents-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Document Library</h1>
          <p className="mt-1 text-sm text-gray-600">Upload and manage quality documents</p>
        </div>
        {hasFullAccess && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
          >
            <UploadSimple size={20} weight="bold" />
            Upload Documents
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
          <div className="flex gap-1">
            {['', 'apqp', 'ppap', 'inspection', 'general'].map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 text-sm rounded-sm transition-colors ${categoryFilter === cat ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {cat === '' ? 'All' : cat.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-sm text-sm"
          >
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.project_name}</option>
            ))}
          </select>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <FileText size={48} className="mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">No documents found. Upload one to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Filename</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Uploaded</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc, idx) => (
                <tr key={doc.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`document-row-${idx}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {isImage(doc) ? (
                        <ImageIcon size={18} className="text-green-600" />
                      ) : (
                        <FileText size={18} className="text-gray-500" />
                      )}
                      <span className="text-sm font-medium text-gray-900">{doc.original_filename}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{getProjectName(doc.related_id)}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-sm uppercase">
                      {doc.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 mono">{formatFileSize(doc.size)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {/* Preview - available for images even in view-only mode */}
                      {isImage(doc) && (
                        <button
                          data-testid={`preview-doc-${idx}`}
                          onClick={() => handlePreview(doc)}
                          className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium px-2 py-1"
                          title="Preview Image"
                        >
                          <Eye size={16} /> Preview
                        </button>
                      )}
                      {hasFullAccess ? (
                        <>
                          <button
                            data-testid={`download-doc-${idx}`}
                            onClick={() => handleDownload(doc)}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium px-2 py-1"
                          >
                            <DownloadSimple size={16} /> Download
                          </button>
                          <button
                            data-testid={`delete-main-doc-${idx}`}
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium px-2 py-1"
                          >
                            <Trash size={16} />
                          </button>
                        </>
                      ) : (
                        !isImage(doc) && <span className="text-xs text-gray-400 italic">View Only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Upload Documents</h2>
              <p className="text-sm text-gray-600 mt-1">Select category and project for the documents</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="general">General</option>
                  <option value="apqp">APQP</option>
                  <option value="ppap">PPAP</option>
                  <option value="inspection">Inspection</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Related Project (Optional)</label>
                <select
                  value={uploadProjectId}
                  onChange={(e) => setUploadProjectId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">-- No Project (General) --</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name} - {p.customer}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Documents linked to a project will only be visible to users with access to that project.
                </p>
              </div>
              <div>
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-sm cursor-pointer hover:bg-gray-50 transition-colors">
                  <UploadSimple size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">{uploading ? 'Uploading...' : 'Click to select files'}</span>
                  <input
                    type="file"
                    data-testid="document-upload-input"
                    multiple
                    onChange={handleUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <button
                onClick={() => setShowUploadModal(false)}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={closePreview}>
          <div className="bg-white rounded-sm max-w-4xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="border-b border-gray-200 p-4 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-900">{previewDoc.original_filename}</h3>
                <p className="text-xs text-gray-500">Preview Only - {hasFullAccess ? 'Click Download to save' : 'Download Not Available'}</p>
              </div>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-200 rounded-sm transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 min-h-[300px]">
              {previewLoading ? (
                <p className="text-gray-500">Loading preview...</p>
              ) : previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt={previewDoc.original_filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : (
                <p className="text-gray-500">Preview not available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
